import { z } from 'zod';

/**
 * Client-side mirror of the Zod schemas in
 * apps/tours/api/src/api/v1/support/support.schemas.ts.
 *
 * Kept manually in sync — when a backend rule changes, update this file
 * too. Duplication is intentional for v1; if the schema becomes large or
 * drifts often we'll lift it to a shared package.
 */

const attachmentSchema = z.object({
  assetUrl: z.string().url(),
  filename: z.string(),
  contentType: z.string(),
});

const ticketBaseFields = {
  title: z
    .string()
    .min(5, 'Title must be at least 5 characters')
    .max(120, 'Title cannot exceed 120 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  attachments: z.array(attachmentSchema).max(10).default([]),
};

// Per-type fields are deliberately optional — only `title` and
// `description` are required. Dropdowns have UI defaults, but the
// schema does not enforce them so the form is forgiving.

export const bugTicketSchema = z.object({
  type: z.literal('bug'),
  ...ticketBaseFields,
  area: z.string().optional(),
  stepsToReproduce: z.string().optional(),
  expected: z.string().optional(),
  actual: z.string().optional(),
  frequency: z.enum(['always', 'sometimes', 'once']).optional(),
  severity: z.enum(['blocker', 'serious', 'minor', 'cosmetic']).optional(),
  startedAt: z.string().optional(),
  url: z.string().url().optional().or(z.literal('').transform(() => undefined)),
});

export const featureTicketSchema = z.object({
  type: z.literal('feature'),
  ...ticketBaseFields,
  audience: z.enum(['guest', 'hotel', 'internal']).optional(),
  problem: z.string().optional(),
  workaround: z.string().optional(),
  acceptanceCriteria: z.string().optional(),
  priority: z.enum(['nice-to-have', 'important', 'urgent']).optional(),
});

export const contentFixTicketSchema = z.object({
  type: z.literal('content-fix'),
  ...ticketBaseFields,
  entityKind: z
    .enum(['attraction', 'tour', 'hotel', 'neighborhood', 'greater-area'])
    .optional(),
  entityIdOrName: z.string().optional(),
  whatsWrong: z.string().optional(),
  whatItShouldSay: z.string().optional(),
  source_link: z.string().optional(),
});

export const questionTicketSchema = z.object({
  type: z.literal('question'),
  ...ticketBaseFields,
  whatYoureTryingToDo: z.string().optional(),
  whatYouTried: z.string().optional(),
  urgency: z.enum(['blocking', 'soon', 'whenever']).optional(),
});

export const dataQualityTicketSchema = z.object({
  type: z.literal('data-quality'),
  ...ticketBaseFields,
  entityKind: z
    .enum(['attraction', 'tour', 'hotel', 'neighborhood', 'greater-area'])
    .optional(),
  entityIdOrName: z.string().optional(),
  fieldName: z.string().optional(),
  expectedValue: z.string().optional(),
  visibility: z.enum(['guest-visible', 'internal-only']).optional(),
});

export const TICKET_SCHEMAS = {
  bug: bugTicketSchema,
  feature: featureTicketSchema,
  'content-fix': contentFixTicketSchema,
  question: questionTicketSchema,
  'data-quality': dataQualityTicketSchema,
} as const;

export type BugTicketFormValues = z.infer<typeof bugTicketSchema>;
export type FeatureTicketFormValues = z.infer<typeof featureTicketSchema>;
export type ContentFixTicketFormValues = z.infer<typeof contentFixTicketSchema>;
export type QuestionTicketFormValues = z.infer<typeof questionTicketSchema>;
export type DataQualityTicketFormValues = z.infer<typeof dataQualityTicketSchema>;
